import { Feed, LockLocked, LockUnlocked, Wallet } from '@factorialco/f0-react/icons/app'
import {
  CycleRouteMetaDocument,
  CycleRouteMetaQuery,
  CycleSelectorDocument,
} from 'generated/resources/graphql'
import moment from 'moment/moment'

import { createGraphqlDataSourceDefinition } from 'modules/core/hooks/useGraphqlDataSource'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { createBreadcrumbSelectDefinition } from 'modules/core/lib/navigation'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { MetaContext, MetaValue } from 'modules/core/lib/navigation/routes'
import { ResourceMeta } from 'modules/core/lib/navigation/types'

import {
  LegacyPayrollRunStatus,
  PayrollRunStatus,
  getPayrollRunStatusText,
  getPayrollRunStatusVariant,
  isPayrollRunLocked,
} from 'modules/compensations/lib/payrollRunStatus'
import { isFeatureEnabled } from 'modules/compensations/lib/utils'

export const buildPaymentActions = (data: CycleRouteMetaQuery) => {
  const isSepaPaymentsFeatureEnabled = isFeatureEnabled(
    'dev_compensations_sepa_payments',
    data.features?.featuresConnection
  )

  const isBankingPaymentRequestsFeatureEnabled = isFeatureEnabled(
    'dev_banking_payment_requests',
    data.features?.featuresConnection
  )

  const totalPaymentRequests = data.compensations.paymentRequestItemsConnection.totalCount ?? 0
  const hasPaymentRequests = totalPaymentRequests > 0

  const totalPendingPayments =
    data.compensations.paymentRequestPendingItemsConnection?.totalCount ?? 0
  const hasPendingPayments = totalPendingPayments > 0

  if (!isSepaPaymentsFeatureEnabled || !isBankingPaymentRequestsFeatureEnabled) {
    return []
  }

  if (hasPaymentRequests) {
    return [
      {
        key: 'payments',
        variant: 'outline',
        label: i18n.t('compensations.payments.title'),
        icon: Wallet,
      },
    ]
  }

  if (hasPendingPayments) {
    return [
      {
        key: 'create-payment',
        variant: 'outline',
        label: i18n.t('compensations.payments.send_to_pay'),
        icon: Wallet,
      },
    ]
  }

  return []
}

export const buildCycleMeta = (
  { watchQuery, params, session }: MetaContext,
  navigation: Navigation<NavigationPaths>
): MetaValue<ResourceMeta> => {
  const { id } = params
  const companyId = session.company?.id

  if (!id) {
    return {
      title: '',
    }
  }

  return watchQuery({
    query: CycleRouteMetaDocument,
    variables: {
      id: Number(id),
      companyId: companyId,
    },
  }).map(({ data }) => {
    const payrollRun = data.compensations.payrollRunsConnection.nodes[0]
    if (!payrollRun) {
      return { title: '' }
    }

    const previousCycle = data.compensations.previousCycle?.nodes[0]
    const nextCycle = data.compensations.nextCycle?.nodes[0]

    const source = createGraphqlDataSourceDefinition({
      document: CycleSelectorDocument,
      mapVariables: () => {
        return {
          // dateFrom: i18nDateToString(new Date(), 'backend'),
          // dateTo: i18nDateToString(new Date(), 'backend'),
        }
      },
      mapData: (queryData) => {
        return queryData.compensations.payrollRunsConnection.nodes
      },
      mapPagination: (queryData) => {
        const connection = queryData.compensations.payrollRunsConnection
        const pageInfo = connection.pageInfo
        return {
          total: 0,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage: pageInfo.hasNextPage ?? false,
        }
      },
      paginationType: 'infinite-scroll',
    })

    const breadcrumbSelect = createBreadcrumbSelectDefinition<
      ReturnType<typeof source.mapData>[number],
      // Todo: fix this type
      typeof source
    >({
      url: (value: string) => navigation.compensations.cycles.id.fullPath({ id: value }),
      mapOptions: (cycleItem) => {
        return {
          label: cycleItem.label,
          value: cycleItem?.id.toString(),
          avatar: {
            type: 'flag' as const,
            flag: cycleItem?.cycle?.country ?? 'es',
          },
        }
      },
      source,
      defaultItem: {
        label: payrollRun.label,
        value: payrollRun.id.toString(),
        avatar: {
          type: 'flag' as const,
          flag: 'es',
        },
      },
    })

    const isLocked = isPayrollRunLocked(payrollRun.status, payrollRun.lockedAt)
    const lockPeriodText = i18n.t('compensations.PayrollRunCompensationsList.lock_period')
    const unlockPeriodText = i18n.t('compensations.PayrollRunCompensationsList.unlock_period')
    const endsOn = stringToDate(payrollRun?.endsOn, 'backend_datetime')
    const lastDayOfMonth = moment().endOf('month').startOf('day')
    const isPastPeriod = endsOn.isBefore(lastDayOfMonth, 'day')
    const isPayrollRunClosed =
      LegacyPayrollRunStatus.CLOSED === payrollRun.status ||
      PayrollRunStatus.COMPLETED === payrollRun.status
    const isFrozen = isPastPeriod || isPayrollRunClosed
    const lockTooltipLabel = isFrozen
      ? i18n.t('compensations.PayrollRunCompensationsList.past_periods_cannot_be_unlocked')
      : isLocked
      ? unlockPeriodText
      : lockPeriodText

    const variant = getPayrollRunStatusVariant(payrollRun.status)

    const status = {
      text: getPayrollRunStatusText(payrollRun.status),
      label: i18n.t('compensations.employee_updates_data_collection_page.columns.status'),
      variant,
    }

    const isTimelineFeatureEnabled = isFeatureEnabled(
      'dev_compensations_payrollrun_timeline',
      data.features?.featuresConnection
    )

    const isCyclesActivityFeatureEnabled = isFeatureEnabled(
      'dev_payroll_cycles',
      data.features?.featuresConnection
    )

    const hasProfessionals = isFeatureEnabled(
      'factorial_professionals',
      data.features?.featuresConnection
    )

    const hasCompensationsProfessionalsConnection = isFeatureEnabled(
      'dev_compensations_professionals_connection',
      data?.features?.featuresConnection
    )

    const canManageActivity =
      data?.compensations?._policies?.compensationActivityManage?.can ?? false

    return {
      title: payrollRun.label,
      headerComponentProps: {
        title: payrollRun.label,
        secondaryActions: [
          {
            key: 'lock-payroll-run',
            variant: 'outline',
            hideLabel: true,
            label: lockTooltipLabel,
            icon: isLocked ? LockLocked : LockUnlocked,
            disabled: isFrozen,
          },
          ...(hasProfessionals &&
          canManageActivity &&
          isCyclesActivityFeatureEnabled &&
          hasCompensationsProfessionalsConnection
            ? [
                {
                  key: 'toggle-cycle-activity',
                  variant: 'outline',
                  label: i18n.t('compensations.PayrollRunCompensationsList.activity'),
                  icon: Feed,
                  disabled: isFrozen,
                },
              ]
            : []),
          ...buildPaymentActions(data),
          ...(isTimelineFeatureEnabled
            ? [
                {
                  key: 'complete-cycle',
                  variant: 'outline',
                  label: i18n.t('compensations.PayrollRunCompensationsList.complete_cycle'),
                  disabled: isFrozen,
                },
              ]
            : []),
        ],
        ...(isTimelineFeatureEnabled && { status }),
        metadata: [
          {
            label: `${i18n.t(
              'compensations.PayrollRunCompensationsList.breadcrumbMetadataLabel.country'
            )}:`,
            value: {
              type: 'text',
              content: `${payrollRun.cycle?.countryLabel} (${payrollRun.cycle?.currency})`,
            },
          },
        ],
      },
      siblingsNavigation: {
        previous: previousCycle
          ? {
              title: previousCycle.label,
              url: navigation.compensations.cycles.id.fullPath({ id: previousCycle.id }),
            }
          : undefined,
        next: nextCycle
          ? {
              title: nextCycle.label,
              url: navigation.compensations.cycles.id.fullPath({ id: nextCycle.id }),
            }
          : undefined,
        select: breadcrumbSelect,
      },
    }
  })
}
